import axios, { AxiosResponse } from 'axios';
import mime from 'mime-types';
// Import Poll from its own module rather than the `@whatsapp-web/src/structures`
// barrel. The barrel is CommonJS (webpack can't tree-shake named exports from
// it), so `import { Poll }` from it dragged all ~25 WWebJS structures into the
// content bundle (app.js) — including Buttons.js/List.js → util/Util.js →
// node-webpmux + fluent-ffmpeg (a Node sticker/media lib the content script
// never uses). Poll.js is a standalone class with zero requires; MessageMedia
// was already imported by subpath. This keeps only the two structures the
// content script actually instantiates. See PERF_AUDIT R11-A.
import Poll from '@whatsapp-web/src/structures/Poll';
import MessageMedia from '@whatsapp-web/src/structures/MessageMedia';
import { getWaMessageType, initWaMessageOptions } from './message-utils';
import { encodeUserMessageIdAsMessageSecret } from '@bt-common/scheduling';
import { prepareSend, type PreparedSend } from '@bt-common/utils/send-prep';
import { UserMessage } from '@bt-common/schemas/bt/user-message';
import { requestAssetBlob } from './fetch-asset-blob';

import { WaMessage } from '@bt-common/interfaces/wa-store-types';

/** The reply target: the contract `quotedMessageId` (prepareSend `quotedKey`),
 *  else the extension UI's own quote handle (`replyData`, set by the compose
 *  box / the AI bridge). */
const resolveQuotedMessageId = (message: UserMessage, prepared: PreparedSend): string | undefined =>
    String(prepared.quotedKey || message?.replyData?._serialized || message?.replyData?.id || '').trim() || undefined;

export async function prepareMessageForWa(message: UserMessage): Promise<WaMessage> {

    const waMessage: WaMessage = {};

    // THE one UserMessage → engine-neutral payload conversion (bt-common
    // prepareSend, plan A.9.6): recipient, text with inline mentions rewritten
    // + missing @tokens injected, mentions, poll, quote, link preview. This file
    // maps only to the wa-js call shape.
    const prepared = prepareSend(message);
    waMessage.type = getWaMessageType(message as any);
    waMessage.chatId = prepared.chatId;
    waMessage.messageId = message._id.toString();


    switch (waMessage.type) {
        case 'text':
        case 'newsletterText':
        case 'image':
        case 'serverImage':
        case 'attachmentWithoutImage':
        case 'serverAttachmentWithoutImage':
        case 'newsletterImage':
        case 'newsletterServerImage':
        case 'newsletterAttachmentWithoutImage':
        case 'newsletterServerAttachmentWithoutImage':
        case 'newsletterAttachmentWithImage':
            const quotedMessageId = resolveQuotedMessageId(message, prepared);
            waMessage.content = prepared.text;
            waMessage.options = {
                ...(quotedMessageId ? { quotedMessageId } : {}),
                // wa-js resolves these into contextInfo.mentionedJid (the
                // highlight + notification).
                mentionedJidList: prepared.mentions,
            };
            const options = waMessage.options;

            if (prepared.linkPreview) {
                options.linkPreview = {
                    title: prepared.linkPreview.title ?? '',
                    description: prepared.linkPreview.description ?? '',
                    canonicalUrl: prepared.linkPreview.url ?? '',
                    thumbnail: prepared.linkPreview.thumbnail ?? '',
                    doNotPlayInline: true
                };
            }

            // Only process asset if it has a valid URL - prevents issues when asset exists but URL is empty/undefined
            if (message.asset && message.asset.url && message.asset.url !== "") {
                // Translate the v1 public `asset.type` (BtPublicMediaKind) into
                // WAWeb's wwebjs send-mode flags. Without these, .gif sends as
                // a still-image-that-falls-back-to-document, voice sends as a
                // regular audio attachment (no PTT bubble), and stickers/
                // documents rely on mimetype heuristics that often misroute.
                // Single source of truth for the mapping — kept here so the
                // wire payload stays declarative (just `kind`) and the page
                // handler owns the wwebjs translation. Mirror this in
                // status-operations.ts if WhatsApp Status ever supports gif.
                const assetKind = String(message.asset.type ?? '').toLowerCase();
                if (assetKind === 'gif') options.sendVideoAsGif = true;
                if (assetKind === 'voice') options.sendAudioAsVoice = true;
                if (assetKind === 'sticker') options.sendMediaAsSticker = true;
                if (assetKind === 'document') options.sendMediaAsDocument = true;

                if (
                    waMessage.type === 'serverImage' ||
                    waMessage.type === 'serverAttachmentWithoutImage' ||
                    waMessage.type === 'newsletterServerImage' ||
                    waMessage.type === 'newsletterServerAttachmentWithoutImage') {
                    const attachment = await MessageMedia.fromUrl(message.asset.url, { unsafeMime: true });
                    attachment.filename = message.asset.name ?? attachment.filename;
                    options.attachment = attachment;
                    options.sendMediaAsHd = true;
                    options.media = {
                        data: attachment.data,
                        mimetype: attachment.mimetype,
                        filename: attachment.filename
                    };
                    options.caption = waMessage.content as string;
                    waMessage.content = '';
                } else {
                    const attachment = await prepareMessageAttachment(message.asset);
                    options.attachment = attachment;
                    options.media = {
                        file: attachment.file,
                        mimetype: attachment.mimetype,
                        filename: attachment.filename
                    };
                    options.caption = waMessage.content as string;
                    waMessage.content = '';
                }
            }
            break;

        case 'poll':
        case 'newsletterPoll':
            waMessage.content = '';
            const pollMessageOptions = await preparePollMessageOptions(message, prepared);

            waMessage.options = pollMessageOptions;
            break;

        default:
            console.warn(`Unsupported message type: ${waMessage.type}`);
            break;
    }

    // The wire messageSecret: bt-common resolveWireSecret (the caller's `secret`
    // when it fits, else `_id`, else minted — prepareSend's wireSecret), encoded
    // with the one encoder both engines use.
    waMessage.messageSecret = encodeUserMessageIdAsMessageSecret(prepared.wireSecret);

    return waMessage; // Return the original message
}

const prepareMediaOptions = async (asset: any) => {
    const base64FromUrl = await axios.get(asset.url, { responseType: 'arraybuffer' })
        .then(response => Buffer.from(response.data, 'binary').toString('base64'));

    return {
        data: base64FromUrl,
        mimeType: asset.type,
        filename: asset.name
    }
}


/**
 * Page-context can't fetch `http://localhost:*` from the HTTPS web.whatsapp.com
 * page (mixed content), and many cross-origin asset URLs lack CORS. Route
 * through the content-script's `fetchAssetBlob` handler (BE#104) — content
 * scripts run in the extension realm, where manifest host_permissions grant
 * the necessary network access. Direct axios is kept as a fallback for the
 * case where the content-script seam isn't wired (e.g. tests).
 */
const prepareMessageAttachment = async (asset: any) => {
    let blob: Blob;
    let mimeFromHeader: string | null = null;

    const proxied = await requestAssetBlob(String(asset.url));
    if (proxied?.success && proxied.data?.dataBase64) {
        const binary = atob(proxied.data.dataBase64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        mimeFromHeader = proxied.data.mimeType || null;
        blob = new Blob([bytes], { type: mimeFromHeader ?? 'application/octet-stream' });
    } else {
        const response: AxiosResponse<Blob> = await axios.get(
            asset.url,
            { responseType: 'blob' }
        );
        blob = response.data;
        // axios v1: response.headers is AxiosResponseHeaders | RawAxiosResponseHeaders.
        // Both expose `content-type` via index access — AxiosHeaders has an
        // index signature `[key: string]: unknown`.
        const headerVal = response.headers['content-type'];
        mimeFromHeader = typeof headerVal === 'string' ? headerVal : null;
    }

    // asset.type from /v1/messages.create is a kind hint ("video", "image",
    // "ptt", ...) — NOT a MIME. Wrapping the blob with `type: "video"` makes
    // WA's prep see an invalid mimetype, decide the file needs repair, and
    // fail with InvalidMediaCheckRepairFailedType. Prefer the real MIME from
    // the HTTP response, falling back to a kind→mime mapping only if the
    // server didn't return one.
    const mimeFromBlob = blob?.type;
    const fallback = mime.lookup(asset.name) || (asset.type && String(asset.type).includes('/') ? asset.type : 'application/octet-stream');
    const resolvedMime = (mimeFromBlob && mimeFromBlob !== 'application/octet-stream' ? mimeFromBlob : null)
        || (mimeFromHeader ? mimeFromHeader.split(';')[0].trim() : null)
        || fallback;

    const assetFile = new File([blob], asset.name, {
        type: resolvedMime,
    });

    const attachment = {
        file: assetFile,
        filename: assetFile.name,
        mimetype: assetFile.type,
    };
    return attachment;
};

const preparePollMessageOptions = async (message: UserMessage, prepared: PreparedSend) => {
    // prepareSend already dropped the empty options, sorted by `order`, and
    // refused a poll without options (invalid_input).
    const poll = prepared.poll!;

    // Same single source as waMessage.messageSecret.
    const messageSecret = encodeUserMessageIdAsMessageSecret(prepared.wireSecret);

    const waPoll = new Poll(
        poll.question,
        poll.options.map((o) => o.text),
        { allowMultipleAnswers: poll.allowMultiple, messageSecret: Array.from(messageSecret) },
    );

    const quotedMessageId = resolveQuotedMessageId(message, prepared);
    const options: any = quotedMessageId ? { quotedMessageId } : {};
    const internalOptions: any = initWaMessageOptions(options);
    internalOptions.poll = waPoll;

    return internalOptions;
};
